import {
	getViemClients,
} from "@bracket/contractor-next";

import bytes32 from "bytes32";

import {
	bigintize,
	getContractWithABI,
	isBlockHash,
	isAddress,
	type Address,
	type BlockHash,
	type ValidChainId,
} from "@bracket/chainography";

import type { Config } from "@wagmi/core";



export declare type BracketVaultWithdrawProps = {
	vault: Address;
	amount: bigint;
	salt?: string;
	salt_bytes?: string;
	chainId?: ValidChainId;
	config?: Config;
	timeout?: number;
}



/**
 * Withdraws funds from a Bracket Vault V2 contract
 * 
 * @param {BracketVaultWithdrawProps} props - The withdrawal parameters
 * @param {Address} props.vault - The vault contract address
 * @param {bigint} props.amount - the cost of the shares valued in the vault's denominating currency
 * @param {string} [props.salt] - Optional salt string for the withdrawal
 * @param {string} [props.salt_bytes] - Optional salt bytes for the withdrawal
 * @param {Config} [props.config] - Optional wagmi config
 * @param {ValidChainId} [props.chainId=1] - The chain ID (defaults to 1)
 * @param {number} [props.timeout=30000] - Transaction timeout in milliseconds
 * @returns {Promise<BlockHash>} The transaction hash
 * @throws {Error} If neither salt nor salt_bytes is provided
 * @throws {Error} If vault address is invalid
 * @throws {Error} If transaction simulation fails
 * @throws {Error} If transaction execution fails
 */
export const withdraw = async ({
	vault,
	amount,
	salt,
	salt_bytes,
	config,
	chainId = 1,
	timeout = 30000,
}: BracketVaultWithdrawProps, ): Promise<BlockHash> => {

	try {

		if(!salt && !salt_bytes)
			throw new Error("neither salt nor salt_bytes provided, provide either.");

		if(!isAddress(vault))
			throw new Error("property vault should be a blockchain addresss");

		const [pclient, wclient] = await getViemClients({ chainId, config, });
		const [_, abi] = getContractWithABI("BracketVaultV2", chainId);

		salt = salt_bytes || bytes32({ input: salt, });

		try {

			const {
				request,
			} = await pclient.simulateContract({
				address: vault,
				abi,
				functionName: "withdraw",
				args: [
					amount,
					salt,
				],
				account: wclient.account,
			});

			console.log("REQ>", request);

			const hash = await wclient.writeContract(request);

			if(isBlockHash(hash)) {
				await pclient.waitForTransactionReceipt({ hash, timeout, });
				return hash;
			}

			throw new Error("expected result to be a bigint");
		}
		catch( err ) {

			throw new Error(`transaction failed - ${err.message}`)
		}
}
	catch( err ) {

		throw new Error(`@bracket/contractor-next::BracketVaultV2::withdraw - ${err.message}`);
	}

}
